// Reload a tab that runs an older build than the web server now serves.
//
// A tab keeps the JavaScript it loaded until it reloads, so after a deploy it goes on
// calling the API the way the old build did, and a route renamed since answers 404.
// Each time the person comes back, this asks for index.html and compares the main bundle
// it names with the one this tab runs. Coming back to the tab (it turns visible again)
// reloads it. Focus alone only offers a reload: closing a file picker or clicking out of
// an iframe focuses the window too, and the person never left.
//
// Ceiling: a tab kept in front across a deploy offers the reload on its next focus and
// takes it on its next return. A draft holding only attachments is not seen as work, so
// the reload drops it.

import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Same-origin and hashed: the dev server's bundle.js has no hash, and a script an
// extension puts in <head> has its own origin.
const MAIN_BUNDLE = /src="(\/static\/js\/main\.[0-9a-f]+\.js)"/;
const TEXT_TYPES = new Set(["text", "textarea", "search", "email", "url", "password", "tel", "number"]);
// A woken laptop's half-dead connection can hold a request open for minutes.
const FETCH_TIMEOUT_MS = 10_000;
// The bundle this tab last reloaded onto. Seeing it served again from the old bundle
// means that reload did not take (a cache answering `/` and the page's URL differently).
const RELOADED_BUNDLE_KEY = "new-build:reloaded-bundle";

/** The hashed main bundle an HTML page loads, or null when it names none. */
export function mainBundle(html: string): string | null {
  return html.match(MAIN_BUNDLE)?.[1] ?? null;
}

/** True when a reload would throw away work: text in a box, or an open dialog or popover. */
function pageHoldsWork(): boolean {
  if (document.querySelector('[role="dialog"]')) return true; // Radix gives popovers this role too
  for (const field of document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input, textarea")) {
    if (TEXT_TYPES.has(field.type) && field.value.trim()) return true;
  }
  return false;
}

/** The bundle the web server serves now. Its own page, not the API, so not `client.ts`. */
async function fetchServedBundle(): Promise<string | null> {
  try {
    const response = await fetch("/", { cache: "no-store", signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });
    return mainBundle(await response.text());
  } catch {
    return null; // offline, mid-deploy or timed out → ask again the next time
  }
}

function alreadyReloadedOnto(bundle: string): boolean {
  try {
    return sessionStorage.getItem(RELOADED_BUNDLE_KEY) === bundle;
  } catch {
    return false; // storage blocked → treated as never reloaded
  }
}

function reloadOnto(bundle: string): void {
  try {
    sessionStorage.setItem(RELOADED_BUNDLE_KEY, bundle);
  } catch {
    // storage blocked → the loop guard can't hold; the reload still goes ahead
  }
  window.location.reload();
}

function offerReload(bundle: string): void {
  toast("A new version is ready", {
    id: "new-build",
    duration: Infinity,
    action: { label: "Reload", onClick: () => reloadOnto(bundle) },
  });
}

/** Checks for a new build each time the person comes back; returns the stop. */
export function initReloadOnNewBuild(queryClient: QueryClient): () => void {
  const ownBundle = mainBundle(document.head.innerHTML);
  if (!ownBundle) {
    if (process.env.NODE_ENV === "production") console.warn("No hashed main bundle in <head>: new-build check off");
    return () => {};
  }

  let checking = false;
  /** The bundle the server serves when it differs from this tab's; one request at a time. */
  async function newBundle(): Promise<string | null> {
    if (checking || document.visibilityState === "hidden") return null;
    checking = true;
    const servedBundle = await fetchServedBundle();
    checking = false;
    return servedBundle && servedBundle !== ownBundle ? servedBundle : null;
  }

  async function onTabShown() {
    const bundle = await newBundle();
    if (!bundle) return;
    if (pageHoldsWork() || queryClient.isMutating() > 0 || alreadyReloadedOnto(bundle)) offerReload(bundle);
    else reloadOnto(bundle);
  }

  async function onWindowFocus() {
    const bundle = await newBundle();
    if (bundle) offerReload(bundle);
  }

  document.addEventListener("visibilitychange", onTabShown);
  window.addEventListener("focus", onWindowFocus);
  return () => {
    document.removeEventListener("visibilitychange", onTabShown);
    window.removeEventListener("focus", onWindowFocus);
  };
}
